import { Bar, BarChart, Cell, ResponsiveContainer, YAxis } from 'recharts'
import type { Incident, MapFilters, Severity } from '@/types/map'
import { severityVar } from '../map-display-utils'
import '../map-console.css'

const ORDER: Severity[] = ['critical', 'high', 'medium', 'low']

export interface SeverityChartPanelProps {
  incidents: Incident[]
  filters: MapFilters
  onToggleSeverity: (severity: Severity) => void
  isLoading?: boolean
}

export function SeverityChartPanel({
  incidents,
  filters,
  onToggleSeverity,
  isLoading = false,
}: SeverityChartPanelProps) {
  if (isLoading) {
    return (
      <section
        aria-busy="true"
        data-testid="severity-chart-skeleton"
        className="soc-panel p-3"
      >
        <div className="mb-2 h-3 w-16 animate-pulse rounded bg-[color:var(--surface-tertiary)]" />
        <div className="flex items-end justify-between gap-1" style={{ height: 100 }}>
          {ORDER.map((_, i) => (
            <div
              key={i}
              className="h-12 flex-1 animate-pulse rounded-sm bg-[color:var(--surface-tertiary)]"
            />
          ))}
        </div>
      </section>
    )
  }

  const counts: Record<Severity, number> = { critical: 0, high: 0, medium: 0, low: 0 }
  for (const inc of incidents) {
    if (inc.severity in counts) counts[inc.severity] = counts[inc.severity] + 1
  }

  const hasData = incidents.length > 0
  const total = ORDER.reduce((sum, s) => sum + counts[s], 0)
  const chartData = ORDER.map((s) => ({
    severity: s,
    count: counts[s],
    active: filters.severities.has(s),
  }))
  const max = Math.max(...ORDER.map((s) => counts[s]), 1)

  return (
    <section className="soc-panel overflow-hidden">
      <header className="soc-panel-head flex items-center justify-between px-3 py-2">
        <h3 className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--text-secondary)]">
          <span className="text-[color:var(--interactive-primary)]" aria-hidden>
            ▸
          </span>
          Severity Breakdown
        </h3>
        {hasData && (
          <span className="font-mono text-[10px] tracking-widest text-[color:var(--text-tertiary)]">
            N={total}
          </span>
        )}
      </header>
      {!hasData ? (
        <p
          data-testid="severity-chart-empty"
          className="px-3 py-3 text-xs text-[color:var(--text-tertiary)]"
        >
          No incidents in range
        </p>
      ) : (
        <div className="p-3">
          <div className="relative" style={{ height: 100 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <YAxis hide domain={[0, max]} />
                <Bar dataKey="count" isAnimationActive={false} radius={[2, 2, 0, 0]}>
                  {chartData.map((d) => (
                    <Cell
                      key={d.severity}
                      fill={severityVar(d.severity)}
                      fillOpacity={d.active ? 1 : 0.3}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-1.5 flex items-stretch justify-between gap-1">
            {ORDER.map((s) => {
              const active = filters.severities.has(s)
              return (
                <button
                  key={s}
                  type="button"
                  data-testid={`bar-${s}`}
                  onClick={() => onToggleSeverity(s)}
                  aria-pressed={active}
                  aria-label={`Toggle ${s} severity (${counts[s]})`}
                  className="flex flex-1 flex-col items-center gap-0.5 rounded-md border px-1 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--interactive-primary)]"
                  style={{
                    borderColor: active ? severityVar(s) : 'var(--border-subtle)',
                    backgroundColor: active
                      ? `color-mix(in oklch, ${severityVar(s)} 12%, transparent)`
                      : 'transparent',
                    opacity: active ? 1 : 0.6,
                  }}
                >
                  <span
                    className="font-mono text-[11px]"
                    style={{ color: active ? severityVar(s) : 'var(--text-secondary)' }}
                  >
                    {counts[s]}
                  </span>
                  <span className="text-[9px] uppercase tracking-[0.16em] text-[color:var(--text-tertiary)]">
                    {s.charAt(0)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </section>
  )
}
